import React, { useState, useEffect, useCallback } from 'react';
import Modal from 'components/GeneralComponents/Modal';
import Button from 'components/GeneralComponents/Button';
import { shiftTemplateService } from './shiftTemplateService';
import moment from 'moment';

const ApplyTemplateModal = ({ visible, template, onCancel, onSuccess, channels = [] }) => {
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [expandedDates, setExpandedDates] = useState([]);

  // Move this block to the top, after useState/useEffect but before generatePreview:
  const normalizedShifts = React.useMemo(() => {
    if (!template?.shift_template_shifts) return [];
    return template.shift_template_shifts.map(shift => {
      if (!shift.group && shift.group_id && Array.isArray(channels)) {
        const groupObj = channels.find(g => g.id === shift.group_id) || null;
        return { ...shift, group: groupObj };
      }
      return shift;
    });
  }, [template, channels]);

  // Generate preview when dates change
  useEffect(() => {
    if (template && startDate && endDate) {
      // Debug: log the shift_template_shifts array
      console.log('shift_template_shifts:', template.shift_template_shifts);
      generatePreview();
    }
  }, [template, startDate, endDate]);

  const generatePreview = useCallback(() => {
    if (!normalizedShifts) return;

    const shifts = [];
    const start = startDate.clone().startOf('day');
    const end = endDate.clone().startOf('day');
    const current = start.clone();

    while (current.isSameOrBefore(end)) {
      normalizedShifts.forEach(shift => {
        // Support both short ('mon') and full ('monday') recurrence names
        const dayShort = current.format('ddd').toLowerCase().substring(0, 3); // 'thu'
        const dayFull = current.format('dddd').toLowerCase(); // 'thursday'
        const recurrenceArr = Array.isArray(shift.recurrence)
          ? shift.recurrence.map(d => d.toLowerCase())
          : [];
        if (recurrenceArr.includes(dayShort) || recurrenceArr.includes(dayFull)) {
          for (let i = 0; i < (shift.shift_quantity || 1); i++) {
            shifts.push({
              date: current.format('MMM DD, YYYY'),
              day: current.format('dddd'),
              time: `${moment(shift.start_time, ['HH:mm', 'hh:mm A']).format('hh:mm A')} - ${moment(shift.end_time, ['HH:mm', 'hh:mm A']).format('hh:mm A')}`,
              position: shift.position || 'No position',
              group: shift.group?.channel_name || (typeof shift.group === 'string' ? shift.group : 'No group'),
              quantity: shift.shift_quantity || 1
            });
          }
        }
      });
      current.add(1, 'day');
    }
    setPreview(shifts);
  }, [normalizedShifts, startDate, endDate]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  }, []);

  const handleApply = async () => {
    if (preview.length === 0) {
      showToast('No shifts will be created for the selected date range', 'error');
      return;
    }

    setLoading(true);
    try {
      await shiftTemplateService.applyTemplate(
        template.id,
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      );
      showToast('Template applied successfully');
      onSuccess();
    } catch (error) {
      console.error('Error applying template:', error);
      showToast(error?.message || 'Failed to apply template', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStartDateChange = (e) => {
    const today = moment().startOf('day');
    let newDate = moment(e.target.value);
    if (!newDate.isValid() || newDate.isBefore(today)) {
      newDate = today;
    }
    setStartDate(newDate);
    if (newDate.isAfter(endDate)) {
      setEndDate(newDate);
    }
  };

  const handleEndDateChange = (e) => {
    let newDate = moment(e.target.value);
    if (!newDate.isValid() || newDate.isBefore(startDate)) {
      newDate = startDate;
    }
    setEndDate(newDate);
  };

  // Group preview shifts by unique properties
  const groupedPreview = React.useMemo(() => {
    const map = new Map();
    preview.forEach(shift => {
      const key = [shift.date, shift.time, shift.position, shift.group].join('|');
      if (!map.has(key)) {
        map.set(key, { ...shift, count: 1 });
      } else {
        map.get(key).count += 1;
      }
    });
    return Array.from(map.values());
  }, [preview]);

  // Group preview by date
  const groupedByDate = React.useMemo(() => {
    const map = new Map();
    groupedPreview.forEach(shift => {
      if (!map.has(shift.date)) map.set(shift.date, []);
      map.get(shift.date).push(shift);
    });
    return Array.from(map.entries()); // [ [date, [shifts...]], ... ]
  }, [groupedPreview]);

  // Initialize expandedDates when preview changes
  useEffect(() => {
    if (groupedByDate.length <= 5) {
      setExpandedDates(groupedByDate.map(([date]) => date));
    } else {
      setExpandedDates([groupedByDate[0]?.[0]]);
    }
  }, [preview.length]);

  const toggleDate = date => {
    setExpandedDates(prev =>
      prev.includes(date)
        ? prev.filter(d => d !== date)
        : [...prev, date]
    );
  };

  if (!visible || !template) return null;

  return (
    <div className="apply-template-modal__wrap modern-modal">
      <div className="modal-header">
        <h2 className="modal-title">Apply Shift Template</h2>
      </div>

      <div className="modal-content">
        <div className="two-column-layout">
          {/* Left Column - Template Details */}
          <div className="left-column">
            {/* Template Details */}
            <div className="template-details-section">
              <h4 className="section-title">Template Details</h4>
              <div className="template-info">
                <div className="info-row">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{template.name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Description:</span>
                  <span className="info-value">{template.description || 'No description'}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Shifts in Template:</span>
                  <span className="info-value">{template.shift_template_shifts?.length || 0} shifts</span>
                </div>
              </div>
              {/* Detailed Shift Information */}
              {normalizedShifts && normalizedShifts.length > 0 && (
                <div className="shifts-details">
                  <h5 className="shifts-subtitle">Shift Details</h5>
                  <div className="shifts-grid">
                    {normalizedShifts.map((shift, index) => (
                      <div key={index} className="shift-card">
                        <div className="shift-header">
                          <span className="shift-number">#{index + 1}</span>
                          {shift.shift_quantity > 1 && (
                            <span className="shift-count-badge">×{shift.shift_quantity}</span>
                          )}
                          <span className="shift-position">{shift.position || 'No position'}</span>
                        </div>
                        {shift.shift_description && (
                          <div className="shift-description-single" title={shift.shift_description}>
                            <span className="description-icon">📝</span>
                            <span>{shift.shift_description}</span>
                          </div>
                        )}
                        <div className="shift-time">
                          <span className="time-icon">🕐</span>
                          <span>{moment(shift.start_time, ['HH:mm', 'hh:mm A']).format('hh:mm A')} - {moment(shift.end_time, ['HH:mm', 'hh:mm A']).format('hh:mm A')}</span>
                        </div>
                        <div className="shift-recurrence">
                          <span className="recurrence-icon">📅</span>
                          <span className="recurrence-days">
                            {(() => {
                              // Sort recurrence days
                              const weekOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
                              const rec = Array.isArray(shift.recurrence) ? shift.recurrence.map(d => d.toLowerCase()) : [];
                              const sorted = weekOrder.filter(day => rec.includes(day));
                              return sorted.map(day => day.charAt(0).toUpperCase() + day.slice(1, 3)).join(', ') || 'No recurrence';
                            })()}
                          </span>
                        </div>
                        {shift.group?.channel_name && (
                          <div className="shift-group">
                            <span className="group-icon">🏢</span>
                            <span>{shift.group.channel_name}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Preview */}
          <div className="right-column">
            <div className="preview-section">
              <h4 className="section-title">
                Preview ({preview.length} shifts will be created)
              </h4>
              {groupedByDate.length > 0 ? (
                <div className="preview-list">
                  {groupedByDate.map(([date, shifts], idx) => (
                    <div key={date} className="preview-date-group">
                      <div className="preview-date-header" onClick={() => toggleDate(date)}>
                        <strong>{shifts[0].day}, {date}</strong>
                        <span className="collapse-toggle">{expandedDates.includes(date) ? '▲' : '▼'}</span>
                      </div>
                      {expandedDates.includes(date) && (
                        <div className="preview-date-shifts">
                          {shifts.map((shift, index) => (
                            <div key={index} className="preview-item">
                              <div className="preview-details">
                                <span className="preview-time">{shift.time}</span>
                                {shift.position && (
                                  <span className="preview-position">• {shift.position}</span>
                                )}
                                {shift.group && (
                                  <span
                                    className="preview-group"
                                    title={shift.group?.channel_name || (typeof shift.group === 'string' ? shift.group : 'No group')}
                                  >
                                    • {shift.group?.channel_name || (typeof shift.group === 'string' ? shift.group : 'No group')}
                                  </span>
                                )}
                                {shift.count > 1 && (
                                  <span className="preview-count-badge">×{shift.count}</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-shifts-warning">
                  <div className="warning-icon">⚠️</div>
                  <div className="warning-content">
                    <h5>No shifts will be created</h5>
                    <p>The selected date range doesn't match the template's recurrence pattern.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Date Range Footer */}
      <div className="date-range-footer">
        <div className="date-range-section">
          <div className="date-inputs">
            <div className="date-input-group">
              <label className="date-label">Start Date:</label>
              <input
                type="date"
                value={startDate.format('YYYY-MM-DD')}
                onChange={handleStartDateChange}
                className="date-input"
                min={moment().format('YYYY-MM-DD')}
              />
            </div>
            <div className="date-input-group">
              <label className="date-label">End Date:</label>
              <input
                type="date"
                value={endDate.format('YYYY-MM-DD')}
                onChange={handleEndDateChange}
                className="date-input"
                min={startDate.format('YYYY-MM-DD')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="modal-actions">
        <Button
          text="Cancel"
          action={onCancel}
          type="secondary"
        />
        <Button
          text={loading ? 'Applying...' : 'Apply Template'}
          action={handleApply}
          type="primary"
          disabled={loading || preview.length === 0}
        />
      </div>

      {/* Toast Message */}
      {toast.show && (
        <div className={`toast-message ${toast.type}`}>
          {toast.message}
        </div>
      )}

      <style jsx>{`
        .apply-template-modal__wrap {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          max-width: 1000px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .modal-header {
          padding: 24px 24px 0 24px;
          border-bottom: 1px solid #e1e5e9;
        }

        .modal-title {
          margin: 0 0 24px 0;
          font-size: 24px;
          font-weight: 600;
          color: #232323;
        }

        .modal-content {
          padding: 24px;
          flex: 1;
          overflow-y: auto;
        }

        .two-column-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          height: 100%;
        }

        .left-column {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .right-column {
          display: flex;
          flex-direction: column;
        }

        .preview-section {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .preview-container {
          flex: 1;
          border: 1px solid #e1e5e9;
          border-radius: 12px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .preview-list {
          flex: 1;
          overflow-y: auto;
          padding: 8px;
        }

        .section-title {
          margin: 0 0 16px 0;
          font-size: 18px;
          font-weight: 600;
          color: #232323;
        }

        .template-details-section,
        .date-range-section,
        .preview-section {
          margin-bottom: 32px;
        }

        .template-info {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
        }

        .info-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #e9ecef;
        }

        .info-row:last-child {
          border-bottom: none;
        }

        .info-label {
          font-weight: 600;
          color: #495057;
        }

        .info-value {
          color: #232323;
          text-align: right;
          max-width: 60%;
          word-break: break-word;
        }

        .shifts-details {
          margin-top: 20px;
        }

        .shifts-subtitle {
          margin: 0 0 12px 0;
          font-size: 16px;
          font-weight: 600;
          color: #495057;
        }

        .shifts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 12px;
        }

        .shift-card {
          background: #fff;
          border: 1px solid #e1e5e9;
          border-radius: 10px;
          padding: 12px;
          transition: all 0.2s ease;
        }

        .shift-card:hover {
          border-color: #0a84ff;
          box-shadow: 0 2px 8px rgba(10,132,255,0.08);
        }

        .shift-header {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          margin-bottom: 8px;
          padding-bottom: 6px;
          border-bottom: 1px solid #f0f0f0;
          gap: 8px;
        }

        .shift-number {
          background: #0a84ff;
          color: white;
          font-size: 12px;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 4px;
        }

        .shift-count-badge {
          background: #0a84ff;
          color: #fff;
          font-size: 13px;
          font-weight: 600;
          border-radius: 8px;
          padding: 2px 8px;
          margin-left: 4px;
          display: inline-block;
        }

        .shift-position {
          font-weight: 600;
          color: #232323;
          font-size: 14px;
          margin-left: auto;
        }

        .shift-description-single {
          font-size: 13px;
          color: #6c757d;
          margin: 4px 0 4px 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 90%;
          cursor: pointer;
        }

        .shift-time,
        .shift-capacity,
        .shift-recurrence,
        .shift-group,
        .shift-description {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 4px;
          font-size: 13px;
          color: #495057;
        }

        .shift-time {
          color: #0a84ff;
          font-weight: 500;
        }

        .shift-capacity {
          color: #28a745;
          font-weight: 500;
        }

        .shift-recurrence {
          color: #6f42c1;
          font-weight: 500;
        }

        .time-icon,
        .capacity-icon,
        .recurrence-icon,
        .group-icon,
        .description-icon {
          font-size: 12px;
          flex-shrink: 0;
        }

        .recurrence-days {
          font-weight: 500;
        }

        .shift-description {
          font-style: italic;
          color: #6c757d;
          margin-top: 4px;
          padding-top: 4px;
          border-top: 1px solid #f0f0f0;
        }

        .date-inputs {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .date-input-group {
          display: flex;
          flex-direction: column;
        }

        .date-label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
        }

        .date-input {
          padding: 12px 16px;
          border: 1.5px solid #e1e5e9;
          border-radius: 10px;
          font-size: 16px;
          background: #fff;
          transition: border 0.2s, box-shadow 0.2s;
        }

        .date-input:focus {
          outline: none;
          border: 1.5px solid #0a84ff;
          box-shadow: 0 2px 8px rgba(10,132,255,0.08);
        }

        .preview-item {
          padding: 12px 4px 12px 4px;
          border-bottom: 1px solid #f0f0f0;
          transition: background 0.2s;
        }

        .preview-item:last-child {
          border-bottom: none;
        }

        .preview-item:hover {
          background: #f8f9fa;
        }

        .preview-date {
          margin-bottom: 4px;
          color: #232323;
        }

        .preview-details {
          display: flex;
          flex-wrap: nowrap;
          gap: 8px;
          font-size: 14px;
          color: #6c757d;
          align-items: center;
          overflow: hidden;
          min-width: 0;
        }

        .preview-group {
          max-width: 180px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: inline-block;
          min-width: 0;
        }

        .preview-time {
          font-weight: 500;
        }

        .no-shifts-warning {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          border-radius: 12px;
          color: #856404;
        }

        .warning-icon {
          font-size: 20px;
          flex-shrink: 0;
        }

        .warning-content h5 {
          margin: 0 0 4px 0;
          font-size: 16px;
          font-weight: 600;
        }

        .warning-content p {
          margin: 0;
          font-size: 14px;
          line-height: 1.4;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          padding: 24px;
          border-top: 1px solid #e1e5e9;
          background: #f8f9fa;
        }

        .toast-message {
          position: fixed;
          bottom: 24px;
          left: 50%;
          transform: translateX(-50%);
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 500;
          z-index: 10000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }

        .toast-message.success {
          background: #28a745;
          color: white;
        }

        .toast-message.error {
          background: #dc3545;
          color: white;
        }

        .preview-count-badge {
          flex-shrink: 0;
          background: #0a84ff;
          color: #fff;
          font-size: 13px;
          font-weight: 600;
          border-radius: 8px;
          padding: 2px 8px;
          margin-left: 8px;
          display: inline-block;
        }

require "sidekiq/web"

        .date-range-footer {
          position: sticky;
          bottom: 0;
          left: 0;
          width: 100%;
          background: #f8f9fa;
          border-top: 1.5px solid #e1e5e9;
          z-index: 10;
          padding: 16px 24px 0 24px;
          margin-bottom: 0;
        }

        .description-icon {
          font-size: 12px;
          flex-shrink: 0;
          margin-right: 4px;
        }

        .preview-date-group {
          border-bottom: 1px solid #f0f0f0;
        }
        .preview-date-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          padding: 10px 0 4px 0;
          font-size: 15px;
          color: #232323;
          background: #f8f9fa;
        }
        .collapse-toggle {
          font-size: 13px;
          color: #0a84ff;
          margin-left: 8px;
        }
        .preview-date-shifts {
          padding-left: 0px;
        }

        @media (max-width: 600px) {
          .two-column-layout {
            grid-template-columns: 1fr;
            gap: 24px;
          }

          .left-column {
            gap: 20px;
          }

          .date-inputs {
            grid-template-columns: 1fr;
          }
          
          .modal-content {
            padding: 16px;
          }
          
          .modal-actions {
            padding: 16px;
          }

          .shifts-grid {
            grid-template-columns: 1fr;
            gap: 8px;
          }

          .shift-card {
            padding: 10px;
          }

          .shift-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .shift-time,
          .shift-capacity,
          .shift-recurrence,
          .shift-group,
          .shift-description {
            font-size: 12px;
          }

          .date-range-footer {
            padding: 12px 8px 0 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default ApplyTemplateModal; 